import { useEffect, useState } from 'react';
import { Truck, User, Phone, Mail, MapPin, FileText, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router';
import { driverService } from '../services/driverService';
import { staticDefinitionService } from '../services/staticDefinitionService';

const normalizeUpper = (value: string) => String(value || '').toLocaleUpperCase('tr-TR');
const normalizePhone = (value: string) => String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
const normalizePlate = (value: string) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
const isValidPlate = (value: string) => {
  const plate = normalizePlate(value);
  return plate.length >= 6 && plate.length <= 9 && /^\d{2}[A-ZÇĞİÖŞÜ]{1,3}\d{2,4}$/.test(plate);
};

interface ApplicationForm {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  tcNo: string;
  birthDate: string;
  city: string;
  district: string;
  address: string;
  
  licenseType: string;
  licenseDate: string;
  srcBelgeNo: string;
  srcBelgeDate: string;
  
  hasVehicle: 'yes' | 'no';
  vehiclePlate?: string;
  vehicleBrand?: string;
  vehicleModel?: string;
  vehicleYear?: string;
  vehicleType?: string;
  vehicleCapacity?: string;
  
  experience: string;
  previousCompanies: string;
  
  notes: string;
}

export function PublicDriverApplication() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState<string>('');
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<string[]>(['TIR', 'KAMYON', 'KAMYONET', 'ÇEKİCİ']);
  const [form, setForm] = useState<ApplicationForm>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    tcNo: '',
    birthDate: '',
    city: '',
    district: '',
    address: '',
    licenseType: 'E',
    licenseDate: '',
    srcBelgeNo: '',
    srcBelgeDate: '',
    hasVehicle: 'no',
    vehiclePlate: '',
    vehicleBrand: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleType: '',
    vehicleCapacity: '',
    experience: '',
    previousCompanies: '',
    notes: '',
  });

  useEffect(() => {
    staticDefinitionService.getByKey('shipment_vehicle_type')
      .then((response: any) => {
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        const values = items.map((item: any) => normalizeUpper(String(item.item_value || item.value || '').trim())).filter(Boolean);
        if (values.length > 0) setVehicleTypeOptions(values);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const normalizedPhone = normalizePhone(form.phone);
      const normalizedVehiclePlate = normalizePlate(form.vehiclePlate || '');
      if (!normalizedPhone) {
        setError('Telefon numarasını başında 0 olmadan ve boşluksuz girin.');
        return;
      }
      if (form.hasVehicle === 'yes' && !isValidPlate(normalizedVehiclePlate)) {
        setError('Plaka boşluksuz 7 veya 8 karakter olmalı ve geçerli formatta girilmelidir.');
        return;
      }
      const applicationData = {
        full_name: `${normalizeUpper(form.firstName)} ${normalizeUpper(form.lastName)}`.trim(),
        phone: normalizedPhone,
        email: String(form.email || '').trim(),
        tc_number: form.tcNo,
        city: normalizeUpper(form.city),
        district: normalizeUpper(form.district),
        address: String(form.address || '').trim(),
        license_number: '',
        license_class: normalizeUpper(form.licenseType || 'E'),
        src_card_number: normalizeUpper(form.srcBelgeNo),
        experience_years: Number(String(form.experience || '0').replace(/\D/g, '')) || 0,
        cekici_plaka: form.hasVehicle === 'yes' ? normalizedVehiclePlate : '',
        dorse_plaka: '',
        vehicle_type: form.hasVehicle === 'yes' ? normalizeUpper(form.vehicleType || '') : '',
        invoice_title: '',
        tax_office: '',
        tax_number: '',
        notes: String(form.notes || '').trim(),
      };

      const response = await driverService.createApplication(applicationData);
      
      if (response.success && response.data) {
        setApplicationId(response.data.id?.toString() || Date.now().toString().slice(-6));
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        setError('Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ApplicationForm, value: string) => {
    let nextValue = value;
    if (['firstName', 'lastName', 'city', 'district', 'vehicleBrand', 'vehicleModel', 'vehicleType', 'previousCompanies'].includes(field)) nextValue = normalizeUpper(value);
    if (field === 'phone') nextValue = normalizePhone(value);
    if (field === 'vehiclePlate') nextValue = normalizePlate(value);
    setForm(prev => ({ ...prev, [field]: nextValue }));
  };

  if (submitted) {
    return (
      <div className="safe-area-page min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Başvurunuz Alındı!</h2>
          <p className="text-gray-600 mb-6">
            Başvurunuz başarıyla kaydedildi. En kısa sürede size geri dönüş yapacağız.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900 font-medium">
              Başvuru Numaranız: #{applicationId}
            </p>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="safe-area-page min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
              <Truck className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Yük Ortağım</h1>
              <p className="text-gray-600">Sürücü Başvuru Formu</p>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>Dış Kaynak Şoför Başvurusu:</strong> Lütfen tüm bilgileri eksiksiz doldurun. 
              Araç sahibiyseniz araç bilgilerinizi de ekleyin.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <User className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Kişisel Bilgiler</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ad *</label>
                <input
                  type="text"
                  required
                  value={form.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Adınız"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Soyad *</label>
                <input
                  type="text"
                  required
                  value={form.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Soyadınız"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">TC Kimlik No *</label>
                <input
                  type="text"
                  required
                  maxLength={11}
                  value={form.tcNo}
                  onChange={(e) => handleChange('tcNo', e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="12345678901"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Doğum Tarihi *</label>
                <input
                  type="date"
                  required
                  value={form.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefon *</label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="05XX XXX XX XX"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">E-posta *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ornek@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">İl *</label>
                <input
                  type="text"
                  required
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="İstanbul"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">İlçe *</label>
                <input
                  type="text"
                  required
                  value={form.district}
                  onChange={(e) => handleChange('district', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Kadıköy"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Adres *</label>
                <textarea
                  required
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tam adresiniz"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Ehliyet ve Belgeler</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ehliyet Sınıfı *</label>
                <select
                  required
                  value={form.licenseType}
                  onChange={(e) => handleChange('licenseType', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="E">E Sınıfı</option>
                  <option value="D">D Sınıfı</option>
                  <option value="C">C Sınıfı</option>
                  <option value="B">B Sınıfı</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ehliyet Alış Tarihi *</label>
                <input
                  type="date"
                  required
                  value={form.licenseDate}
                  onChange={(e) => handleChange('licenseDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SRC Belge No *</label>
                <input
                  type="text"
                  required
                  value={form.srcBelgeNo}
                  onChange={(e) => handleChange('srcBelgeNo', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="SRC belge numarası"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SRC Alış Tarihi *</label>
                <input
                  type="date"
                  required
                  value={form.srcBelgeDate}
                  onChange={(e) => handleChange('srcBelgeDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Truck className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Araç Bilgileri</h2>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Kendi aracınız var mı? *</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasVehicle"
                    value="yes"
                    checked={form.hasVehicle === 'yes'}
                    onChange={(e) => handleChange('hasVehicle', e.target.value as 'yes' | 'no')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Evet, kendi aracım var</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasVehicle"
                    value="no"
                    checked={form.hasVehicle === 'no'}
                    onChange={(e) => handleChange('hasVehicle', e.target.value as 'yes' | 'no')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Hayır, araç sahibi değilim</span>
                </label>
              </div>
            </div>

            {form.hasVehicle === 'yes' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Plaka *</label>
                  <input
                    type="text"
                    required={form.hasVehicle === 'yes'}
                    value={form.vehiclePlate}
                    onChange={(e) => handleChange('vehiclePlate', e.target.value.toUpperCase())}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="34 ABC 1234"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Marka *</label>
                  <input
                    type="text"
                    required={form.hasVehicle === 'yes'}
                    value={form.vehicleBrand}
                    onChange={(e) => handleChange('vehicleBrand', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mercedes, Ford, vb."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                  <input
                    type="text"
                    required={form.hasVehicle === 'yes'}
                    value={form.vehicleModel}
                    onChange={(e) => handleChange('vehicleModel', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Actros, Cargo, vb."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model Yılı *</label>
                  <input
                    type="text"
                    required={form.hasVehicle === 'yes'}
                    value={form.vehicleYear}
                    onChange={(e) => handleChange('vehicleYear', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="2020"
                    maxLength={4}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Araç Tipi *</label>
                  <select
                    required={form.hasVehicle === 'yes'}
                    value={form.vehicleType}
                    onChange={(e) => handleChange('vehicleType', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seçin</option>
                    {vehicleTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Yük Kapasitesi (ton) *</label>
                  <input
                    type="text"
                    required={form.hasVehicle === 'yes'}
                    value={form.vehicleCapacity}
                    onChange={(e) => handleChange('vehicleCapacity', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="24"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Deneyim Bilgileri</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Toplam Deneyim (Yıl) *</label>
                <input
                  type="text"
                  required
                  value={form.experience}
                  onChange={(e) => handleChange('experience', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: 5 yıl"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Daha Önce Çalıştığınız Firmalar</label>
                <textarea
                  value={form.previousCompanies}
                  onChange={(e) => handleChange('previousCompanies', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Firma adları ve çalışma süreleri"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ek Notlar</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Eklemek istediğiniz bilgiler"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <Link
                to="/login"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Giriş Sayfasına Dön
              </Link>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  'Başvuruyu Gönder'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
